// crewbit-admin/src/hooks/useInvoiceActivity.ts
//
// Entity-scoped activity_log read for §InvoiceDetail timeline (Session 125).
// The pre-existing useActivityLog(businessId) hook serves the team Activity tab
// — different shape (paginated, business-scoped). This hook is the narrow
// invoice-scoped read the slide-over needs: latest 25 rows, no pagination,
// MONEY_MUTATION_EVENT-invalidated.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';

export interface InvoiceActivityRow {
  id: string;
  activity_type: string;
  description: string;
  entity_type: string;
  entity_id: string;
  logged_by: string | null;
  created_at: string;
}

const SELECT_COLS =
  'id, activity_type, description, entity_type, entity_id, logged_by, created_at';
const ROW_LIMIT = 25;

export interface UseInvoiceActivityReturn {
  data: InvoiceActivityRow[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useInvoiceActivity(invoiceId: string | null): UseInvoiceActivityReturn {
  const [data, setData] = useState<InvoiceActivityRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const doFetch = useCallback(async () => {
    if (!invoiceId) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLoading(true);
    try {
      const { data: rows, error: queryError } = await supabase
        .from('activity_log')
        .select(SELECT_COLS)
        .eq('entity_type', 'invoice')
        .eq('entity_id', invoiceId)
        .order('created_at', { ascending: false })
        .limit(ROW_LIMIT);

      if (!mountedRef.current) return;
      if (queryError) {
        setError(new Error(queryError.message));
      } else {
        setData((rows ?? []) as unknown as InvoiceActivityRow[]);
        setError(null);
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [invoiceId]);

  useEffect(() => {
    setData(null);
    setError(null);
  }, [invoiceId]);

  useEffect(() => {
    if (!invoiceId) return;
    void doFetch();
  }, [invoiceId, doFetch]);

  useEffect(() => {
    const onMutation = () => { if (invoiceId) void doFetch(); };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    return () => { window.removeEventListener(MONEY_MUTATION_EVENT, onMutation); };
  }, [invoiceId, doFetch]);

  const refetch = useCallback(async () => { await doFetch(); }, [doFetch]);

  return { data, isLoading, error, refetch };
}
